import { DatabaseService } from './DatabaseService';
import type { Product, ProductOperationResult } from '@smartphone-tycoon/shared-core';

export class ProductManager {
  static readonly COLLECTION = 'products';

  static async discontinueProduct(id: string): Promise<ProductOperationResult> {
    try {
      const product = await DatabaseService.get(this.COLLECTION, id);
      
      if (!product) {
        return {
          success: false,
          error: 'Product not found'
        };
      }

      await DatabaseService.update(this.COLLECTION, id, { status: 'discontinued' });
      return {
        success: true,
        data: { ...product, status: 'discontinued' }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to discontinue product'
      };
    }
  }


  static async getProduct(id: string): Promise<ProductOperationResult> {
    try {
      const product = await DatabaseService.get(this.COLLECTION, id);
      
      if (!product) {
        return {
          success: false,
          error: 'Product not found'
        };
      }

      return {
        success: true,
        data: product
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get product'
      };
    }
  }

  static async getProducts(): Promise<Product[]> {
    try {
      const products = await DatabaseService.getAll(this.COLLECTION);
      return products || [];
    } catch (error) {
      console.error('Failed to get products:', error);
      return [];
    }
  }

  static async updateSalesMetrics(
    id: string, 
    metrics: {
      unitsSold?: number;
      revenue?: number;
      averageRating?: number;
      marketShare?: number;
    }
  ): Promise<ProductOperationResult> {
    try {
      const product = await DatabaseService.get(this.COLLECTION, id);
      
      if (!product) {
        return {
          success: false,
          error: 'Product not found'
        };
      }

      const updatedSalesMetrics = {
        ...product.salesMetrics,
        ...metrics
      };

      await DatabaseService.update(this.COLLECTION, id, { 
        salesMetrics: updatedSalesMetrics 
      });
      
      return {
        success: true,
        data: { ...product, salesMetrics: updatedSalesMetrics }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update sales metrics'
      };
    }
  }

  static async updateProduct(id: string, updates: Partial<Product>): Promise<ProductOperationResult> {
    try {
      const product = await DatabaseService.get(this.COLLECTION, id);
      
      if (!product) {
        return {
          success: false,
          error: 'Product not found'
        };
      }

      await DatabaseService.update(this.COLLECTION, id, updates);
      
      return {
        success: true,
        data: { ...product, ...updates }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update product'
      };
    }
  }

  static async createProduct(data: Omit<Product, 'id'>): Promise<ProductOperationResult> {
    try {
      // Ensure sales metrics are properly initialized for new products
      const productData = {
        ...data,
        salesMetrics: {
          unitsSold: 0,
          revenue: 0,
          averageRating: 0,
          marketShare: 0,
          ...data.salesMetrics // Allow override if needed, but default to 0
        },
        createdAt: Date.now()
      };

      const id = await DatabaseService.add(this.COLLECTION, productData);

      return {
        success: true,
        data: { id, ...productData }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create product'
      };
    }
  }

  static async getAllProducts(): Promise<ProductOperationResult> {
    try {
      const products = await DatabaseService.getAll(this.COLLECTION);
      
      return {
        success: true,
        data: products
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get all products'
      };
    }
  }

  static async getProductsByCompany(companyId: string): Promise<ProductOperationResult> {
    try {
      const allProducts = await DatabaseService.getAll(this.COLLECTION);
      const companyProducts = allProducts.filter(product => 
        product.metadata?.companyId === companyId
      );
      
      return {
        success: true,
        data: companyProducts
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get company products'
      };
    }
  }

  static async clearAllProducts(): Promise<ProductOperationResult> {
    try {
      await DatabaseService.clear(this.COLLECTION);
      // console.log('🗑️ All products cleared from database');
      
      return {
        success: true,
        data: []
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear products'
      };
    }
  }

  static async clearCompetitorProducts(): Promise<ProductOperationResult> {
    try {
      const allProducts = await DatabaseService.getAll(this.COLLECTION);
      const competitorProducts = allProducts.filter(product => 
        product.metadata?.companyId && product.metadata.companyId !== 'player'
      );

      // Delete each competitor product
      for (const product of competitorProducts) {
        await DatabaseService.delete(this.COLLECTION, product.id);
      }

      // console.log(`🗑️ Cleared ${competitorProducts.length} competitor products from database`);
      
      return {
        success: true,
        data: competitorProducts
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear competitor products'
      };
    }
  }

  static async startProduction(id: string, gameDate: { day: number; month: number; year: number }): Promise<ProductOperationResult> {
    try {
      const product = await DatabaseService.get(this.COLLECTION, id);
      
      if (!product) {
        return {
          success: false,
          error: 'Product not found'
        };
      }

      const updates = {
        status: 'production',
        metadata: {
          ...product.metadata,
          releaseDate: gameDate
        }
      };

      await DatabaseService.update(this.COLLECTION, id, updates);
      
      return {
        success: true,
        data: { ...product, ...updates }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start production'
      };
    }
  }
}

// Debug helpers for browser console
(window as unknown as { debugProducts: unknown }).debugProducts = {
  async getAllProducts() {
    const result = await ProductManager.getAllProducts();
    console.log('🔧 Debug: All products in database:', result);
    return result;
  },
  
  async getPlayerProducts() {
    const result = await ProductManager.getProductsByCompany('player');
    console.log('🔧 Debug: Player products:', result);
    return result;
  },
  
  async getCompetitorProducts(competitorId?: string) {
    if (competitorId) {
      const result = await ProductManager.getProductsByCompany(competitorId);
      console.log(`🔧 Debug: ${competitorId} products:`, result);
      return result;
    } else {
      const allResult = await ProductManager.getAllProducts();
      if (allResult.success && allResult.data) {
        const competitors = allResult.data.filter(p => p.metadata?.companyId !== 'player');
        console.log('🔧 Debug: All competitor products:', competitors);
        return competitors;
      }
      return [];
    }
  }
};